import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection, importProvidersFrom } from '@angular/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { ToastrModule } from 'ngx-toastr';
import { provideMarkdown } from 'ngx-markdown';
import { provideKeycloak, withAutoRefreshToken } from 'keycloak-angular';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';

import { routes } from './app.routes';
import { AuthInitService } from './core/providers/auth-init.service';
import { EnvironmentService } from './core/services/environment.service';
import { EventsManagerService } from './core/services/events-manager.service';
import { CartoafEnv } from './core/models/env';

import { cacheCartoafInterceptor } from './core/interceptors/cache-cartoaf.interceptor';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { responseErrorInterceptor } from './core/interceptors/response-error.interceptor';
import { httpCancelInterceptor } from './core/interceptors/http-cancel.interceptor';
import { applicationInterceptor } from './core/interceptors/application.interceptor';

registerLocaleData(localeIt);

export function createAppConfig(env: CartoafEnv): ApplicationConfig {
  return {
    providers: [
      provideZonelessChangeDetection(),
      provideRouter(routes, withHashLocation()),
      provideHttpClient(
        withInterceptors([
          applicationInterceptor,
          cacheCartoafInterceptor,
          tokenInterceptor,
          responseErrorInterceptor,
          httpCancelInterceptor,
        ])
      ),
      provideMarkdown({ loader: HttpClient }),
      importProvidersFrom(
        ToastrModule.forRoot()
      ),
      DatePipe,
      EventsManagerService,
      {
        provide: EnvironmentService,
        useFactory: () => {
          const svc = new EnvironmentService();
          svc.setSync(env);
          return svc;
        }
      },
      ...(env?.auth_provider === 'keycloak' ? [
        provideKeycloak({
          config: {
            url: env.keycloak!.url,
            realm: env.keycloak!.realm,
            clientId: env.keycloak!.clientId,
          },
          features: [withAutoRefreshToken({ onInactivityTimeout: 'logout', sessionTimeout: 300 })]
        })
      ] : []),
      provideAppInitializer(() => {
        inject(NgbTooltipConfig).container = 'body';
      }),
      provideAppInitializer(() => {
        const authInit = inject(AuthInitService);
        return authInit.init();
      }),
      {
        provide: 'IAuthService',
        useFactory: (authInitService: AuthInitService) => authInitService.service,
        deps: [AuthInitService],
      },
    ],
  };
}
